import React, {PropsWithChildren, useEffect, useRef, useState, useContext} from 'react';
import styled from 'styled-components';
import {maxWidth} from '../../styled-components/windowIsWeb';
import {
  ContentData,
  getContentHtml,
  savedContentTimestampKey,
  simplifyContentId,
} from '../../utils/planFormEmail';
import {windowIsWeb, windowIsWebDesktop} from '../../styled-components/windowIsWeb';
import PlansContext from '../../contexts/PlansContext';
import {withContentRevalidation} from '../../utils/contentAssetUrl';
interface WebViewProps {
  source?: any;
  onLoad?: () => void;
  paddingBottom?: string;
  remoteFetchDisabled?: boolean;
  remoteFetchError?: string;
}

// A publish can update plan metadata before the replacement object is visible
// through the CDN. Revalidate briefly in the background so an already-open
// page converges on the published content without relying on a route change.
const remoteContentRevalidationDelays = [1500, 4000];

const getStartedMediaElement = (
  element: Element,
): HTMLMediaElement | null => {
  const tagName = element.tagName.toLowerCase();
  const mediaElement =
    tagName === 'audio' || tagName === 'video'
      ? (element as HTMLMediaElement)
      : (element.closest('audio, video') as HTMLMediaElement | null);

  return mediaElement && (!mediaElement.paused || mediaElement.currentTime > 0)
    ? mediaElement
    : null;
};

const containsStartedMedia = (contentBody: HTMLElement): boolean =>
  Array.from(contentBody.querySelectorAll('audio, video')).some(
    element => !!getStartedMediaElement(element),
  );

const getLegacyContentRecency = (contentId: string): number => {
  const savedAt = Number(
    window.localStorage.getItem(savedContentTimestampKey(contentId)),
  );
  if (Number.isFinite(savedAt) && savedAt > 0) {
    return savedAt;
  }

  try {
    const url = new URL(contentId);
    const revision = url.searchParams.get('gccRevision');
    const revisionTime = revision
      ? Number(revision) || Date.parse(revision)
      : 0;
    if (Number.isFinite(revisionTime) && revisionTime > 0) {
      return revisionTime;
    }

    const policy = url.searchParams.get('Policy');
    if (policy) {
      const normalizedPolicy = policy
        .replace(/-/g, '+')
        .replace(/_/g, '=')
        .replace(/~/g, '/');
      const paddedPolicy = normalizedPolicy.padEnd(
        Math.ceil(normalizedPolicy.length / 4) * 4,
        '=',
      );
      const decodedPolicy = JSON.parse(window.atob(paddedPolicy));
      const expiresAt = Number(
        decodedPolicy?.Statement?.[0]?.Condition?.DateLessThan?.[
          'AWS:EpochTime'
        ],
      );
      if (Number.isFinite(expiresAt) && expiresAt > 0) {
        return expiresAt * 1000;
      }
    }
  } catch (error) {
    // Fall back to the current signed URL, then storage order, below.
  }

  return 0;
};

const ScrollWrapper = styled.div`
  position: relative;
  display:flex;
  flex-direction: column;
  flex: 1;
`;

const ContentWrapper = styled.div<WebViewProps>`
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding-left: 16px;
  padding-right: 16px;
  ${windowIsWeb && !windowIsWebDesktop &&
  `
    padding-bottom: 142px;
  `};
  ${windowIsWeb && windowIsWebDesktop &&
  `
    padding-bottom: 54px;
  `};
`;

const Content = styled.div`
  max-width: ${maxWidth.container};
`;

const ContentBody = styled.div`
  width: 100%;
`;

const CustomEmailFields = styled.div`
  display: none;
  flex-direction: column;
  margin: 16px 0 124px;
  padding: 8px 22px;
  box-shadow: inset 0 0 0 2px rgba(0,0,0,0.05);
  background: rgba(0, 105, 146, 0.05);

  &.is-visible {
    display: flex;
  }

  p {
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.44;
    opacity: 0.9;
  }

  label {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    font-size: 13px;
    font-weight: 600;

    span {
      display: flex;
      &.title {
        align-items: flex-end;
        .small {
          margin-left: 10px;
          margin-bottom: 1px;
          font-size: 10px;
          opacity: 0.6;
        }
      }
    }

    .input-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: 4px;
      padding:4px;
      border-radius: 2px;
      box-shadow:0 0 0 1px rgba(0,0,0,0.1);
      background: #fff;
      cursor: text;

      .static-addresses {
        display: none;
        margin-bottom: 4px;
        padding: 4px 6px;
        font-weight: 500;
        font-size: 11px;
        border-radius: 2px;
        background: rgba(0,0,0,0.1);
        opacity: 0.8;
        cursor: default;
      }

      input {
        border: none;
        width: 96%;
        padding: 4px 4px;
        &::placeholder { opacity: 0.6; }
      }

    }
  }
`;

export const WebView = ({
  source: sourceRoot,
  paddingBottom,
  remoteFetchDisabled = false,
  remoteFetchError,
}: PropsWithChildren<WebViewProps>) => {
  const context = useContext(PlansContext);
  const [resetNum, setResetNum] = useState(0);
  const sourceUri =
    typeof sourceRoot === 'string' ? '' : sourceRoot?.uri || '';
  const inlineSource = typeof sourceRoot === 'string' ? sourceRoot : '';

  const customEmailStaticAddressesRef = useRef<HTMLDivElement>(null);
  const customEmailToInputRef = useRef<HTMLInputElement>(null);
  const customEmailFromInputRef = useRef<HTMLInputElement>(null);
  const contentBodyRef = useRef<HTMLDivElement>(null);
  // include form submit email section or not
  const [planHasForm, setPlanHasForm] = useState(false);
  useEffect(() => {
    const customEmailStaticAddresses = customEmailStaticAddressesRef.current;
    const customEmailToInput = customEmailToInputRef.current;
    const customEmailFromInput = customEmailFromInputRef.current;
    const contentBody = contentBodyRef.current;
    // find saved content via id if exists
    const contentId = simplifyContentId(sourceUri);
    let savedContent = window.localStorage.getItem(contentId);
    if (!savedContent && contentId) {
      // Older builds included signed query parameters in the storage key.
      // Rank matching snapshots by their saved timestamp, content revision, or
      // signed-policy expiry so migration does not restore an older edit.
      const currentLegacyContentId = sourceUri
        .replace(/(file:\/\/\/plans\/)/gi, '')
        .replace(/(.html)/gi, '');
      const legacySnapshots: {
        content: string;
        current: boolean;
        order: number;
        recency: number;
      }[] = [];

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const legacyContentId = window.localStorage.key(index);
        if (
          legacyContentId &&
          !legacyContentId.endsWith(':gccSavedAt') &&
          (legacyContentId === currentLegacyContentId ||
            legacyContentId.startsWith(`${contentId}?`) ||
            legacyContentId.startsWith(`${contentId}#`))
        ) {
          const legacyContent = window.localStorage.getItem(legacyContentId);
          if (legacyContent) {
            legacySnapshots.push({
              content: legacyContent,
              current: legacyContentId === currentLegacyContentId,
              order: index,
              recency: getLegacyContentRecency(legacyContentId),
            });
          }
        }
      }

      legacySnapshots.sort(
        (a, b) =>
          b.recency - a.recency ||
          Number(b.current) - Number(a.current) ||
          b.order - a.order,
      );
      savedContent = legacySnapshots[0]?.content || null;

      if (savedContent) {
        window.localStorage.setItem(contentId, savedContent);
        window.localStorage.setItem(
          savedContentTimestampKey(contentId),
          Date.now().toString(),
        );
      }
    }
    let cancelled = false;
    let renderedContent: string | undefined;
    let lastAppliedRequest = 0;
    let nextRequest = 0;
    let pendingRequests = 0;
    let revalidationExhausted = false;
    let lastLoadError: Error | null = null;
    let loadErrorNotified = false;
    let formIsDirty = false;
    let markFormDirty: ((event: Event) => void) | undefined;
    let formListenerTarget: HTMLElement | null = null;
    const loadToken = Date.now().toString();
    const revalidationTimers: number[] = [];
    // if react is aware of the render container
    if (contentBody) {
      // prevent infinite appending
      contentBody.innerHTML = '';
      markFormDirty = (event: Event) => {
        const target = event.target as Element | null;
        if (
          target?.matches(
            'input, textarea, select, [contenteditable="true"]',
          )
        ) {
          formIsDirty = true;
        }
      };
      formListenerTarget = contentBody;
      contentBody.addEventListener('input', markFormDirty);
      contentBody.addEventListener('change', markFormDirty);
      // helper function appends contentBody to ui
      const appendContent = (html: string) => {
        const replacingRenderedContent =
          renderedContent !== undefined && html !== renderedContent;
        const customEmailFieldsAreDirty = !!(
          (customEmailToInput as HTMLInputElement | null)?.value ||
          (customEmailFromInput as HTMLInputElement | null)?.value
        );
        const replacementWouldInterruptInteraction =
          replacingRenderedContent &&
          (formIsDirty ||
            customEmailFieldsAreDirty ||
            containsStartedMedia(contentBody!));

        if (
          contentBody &&
          !cancelled &&
          html !== renderedContent &&
          !replacementWouldInterruptInteraction
        ) {
          contentBody.innerHTML = '';
          setPlanHasForm(false);
          if (customEmailStaticAddresses) {
            customEmailStaticAddresses.innerHTML = '';
            customEmailStaticAddresses.style.display = 'none';
          }
          // construct data object to construct DOM and functions
          const contentData: ContentData = {
            contentBody,
            id: contentId,
            parsedContent: new window.DOMParser().parseFromString(
              html,
              'text/html',
            ),
            source: html,
            customEmailToInput,
            customEmailFromInput,
            customEmailStaticAddresses,
            sendNotification: (type: 'alert' | 'info', message: string) => context!.sendNotification(type, message),
            setPlanHasForm,
            bumpReset: () => setResetNum(resetNum + 1),
          };
          // add saved content to ui contentBody
          const sourceContent = getContentHtml(contentData);
          if (sourceContent) contentBody.append(sourceContent);
          renderedContent = html;
        }
      };

      const refreshEmbeddedAssets = (attempt: number) => {
        if (!contentBody || !sourceUri) {
          return;
        }

        try {
          const contentSourceUrl = new URL(sourceUri);
          const mediaElementsToReload = new Set<HTMLMediaElement>();
          const assetAttributes = [
            {selector: 'img[src], audio[src], video[src], source[src]', attribute: 'src'},
            {selector: 'video[poster]', attribute: 'poster'},
          ];

          assetAttributes.forEach(({selector, attribute}) => {
            contentBody!.querySelectorAll(selector).forEach(element => {
              const originalAttribute = `data-gcc-original-${attribute}`;
              const originalValue =
                element.getAttribute(originalAttribute) ||
                element.getAttribute(attribute);

              if (
                !originalValue ||
                originalValue.startsWith('blob:') ||
                originalValue.startsWith('data:') ||
                getStartedMediaElement(element)
              ) {
                return;
              }

              const assetUrl = new URL(originalValue, contentSourceUrl);
              if (assetUrl.origin !== contentSourceUrl.origin) {
                return;
              }

              ['Policy', 'Key-Pair-Id', 'Signature'].forEach(parameter => {
                const value = contentSourceUrl.searchParams.get(parameter);
                if (value) {
                  assetUrl.searchParams.set(parameter, value);
                }
              });

              const contentRevision = contentSourceUrl.searchParams.get(
                'gccRevision',
              );
              if (contentRevision) {
                assetUrl.searchParams.set('gccRevision', contentRevision);
              }

              element.setAttribute(originalAttribute, originalValue);
              element.setAttribute(
                attribute,
                withContentRevalidation(
                  assetUrl.toString(),
                  loadToken,
                  attempt,
                ),
              );

              if (element.tagName.toLowerCase() === 'source') {
                const mediaElement = element.closest('audio, video') as
                  | HTMLMediaElement
                  | null;
                if (
                  mediaElement &&
                  mediaElement.paused &&
                  mediaElement.currentTime === 0
                ) {
                  mediaElementsToReload.add(mediaElement);
                }
              }
            });
          });

          // Updating a nested <source> does not make browsers rerun media
          // selection. Reload only idle media so retries cannot interrupt
          // playback that the user has already started.
          mediaElementsToReload.forEach(mediaElement => mediaElement.load());
        } catch (error) {
          // Leave third-party or malformed embedded URLs unchanged.
        }
      };

      const maybeNotifyLoadError = () => {
        if (
          !cancelled &&
          revalidationExhausted &&
          pendingRequests === 0 &&
          lastAppliedRequest === 0 &&
          lastLoadError &&
          !loadErrorNotified
        ) {
          loadErrorNotified = true;
          context!.sendNotification(
            'alert',
            lastLoadError.message || 'Unable to load content',
          );
        }
      };

      const fetchRemoteContent = () => {
        const requestNumber = ++nextRequest;
        const requestUrl = withContentRevalidation(
          sourceUri,
          loadToken,
          requestNumber,
        );
        pendingRequests += 1;
        fetch(requestUrl, {cache: 'no-store'})
          .then(resp => {
            if (!resp.ok) {
              throw new Error(`Unable to load content (${resp.status})`);
            }

            return resp.text();
          })
          .then(resp => {
            // A slow earlier request must not overwrite a later revalidation.
            if (!cancelled && requestNumber > lastAppliedRequest) {
              lastAppliedRequest = requestNumber;
              appendContent(resp);
              refreshEmbeddedAssets(requestNumber);
            }
          })
          .catch(error => {
            lastLoadError =
              error instanceof Error
                ? error
                : new Error('Unable to load content');
          })
          .finally(() => {
            pendingRequests -= 1;
            maybeNotifyLoadError();
          });
      };
      // If saved source is found in localstorage
      // it's assumed to be a string of a simple html page (about.html, t&c.html)
      // otherwise source is an object with a url, fetch from server.
      savedContent
        ? (() => {
            appendContent(savedContent);
            if (!remoteFetchDisabled) {
              refreshEmbeddedAssets(0);
            }
          })()
        : inlineSource
        ? appendContent(inlineSource)
        : sourceUri && !remoteFetchDisabled
        ? (() => {
            fetchRemoteContent();
            remoteContentRevalidationDelays.forEach((delay, index) => {
              revalidationTimers.push(
                window.setTimeout(() => {
                  if (
                    index ===
                    remoteContentRevalidationDelays.length - 1
                  ) {
                    revalidationExhausted = true;
                  }
                  fetchRemoteContent();
                }, delay),
              );
            });
          })()
        : remoteFetchDisabled
        ? (() => {
            setPlanHasForm(false);
            contentBody.textContent =
              remoteFetchError || 'Unable to load content signature';
          })()
        : '';
    }

    return () => {
      cancelled = true;
      revalidationTimers.forEach(timer => window.clearTimeout(timer));
      if (formListenerTarget && markFormDirty) {
        formListenerTarget.removeEventListener('input', markFormDirty);
        formListenerTarget.removeEventListener('change', markFormDirty);
      }
    };
  }, [
    inlineSource,
    remoteFetchDisabled,
    remoteFetchError,
    resetNum,
    sourceUri,
  ]);

  return (
    <ScrollWrapper>
      <ContentWrapper paddingBottom={paddingBottom}>
        <Content>

          <ContentBody ref={contentBodyRef} />

          <CustomEmailFields className={planHasForm ? 'is-visible' : ''}>
            <p>Send the form to additional people. (Comma-separate multiple emails)</p>
            <label>
              <span className="title">
                Send To:
              </span>
              <div className="input-wrapper">
                <div className="static-addresses" ref={customEmailStaticAddressesRef}></div>
                <input type="text" ref={customEmailToInputRef}
                  placeholder="-- Additional TO email addresses --" />
              </div>
            </label>

            <p>Let receivers know who sent the form. (Comma-separate multiple emails)</p>
            <label>
              <span className="title">
                Sent From: <span className="small">(optional)</span>
              </span>
              <div className="input-wrapper">
                <input type="text" ref={customEmailFromInputRef}
                  placeholder="-- Additional FROM email addresses --" />
              </div>
            </label>
          </CustomEmailFields>

        </Content>
      </ContentWrapper>
    </ScrollWrapper>
  );
};

export default WebView;
